import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Phone, Mail, MapPin, Globe, FileText, Plus, Edit, Trash2, Search } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import { loadContractors, createContractor, updateContractor, deleteContractor } from '../utils/storage';

const ContractorDirectory = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingContractor, setDeletingContractor] = useState(null);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    notes: ''
  });

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    setLoading(true);
    const data = await loadContractors();
    setContractors(data);
    setLoading(false);
  };

  const handleOpenDialog = (contractor = null) => {
    if (contractor) {
      setEditingContractor(contractor);
      setFormData({
        companyName: contractor.companyName || '',
        contactName: contractor.contactName || '',
        phone: contractor.phone || '',
        email: contractor.email || '',
        address: contractor.address || '',
        website: contractor.website || '',
        notes: contractor.notes || ''
      });
    } else {
      setEditingContractor(null);
      setFormData({
        companyName: '',
        contactName: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        notes: ''
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingContractor(null);
    setFormData({
      companyName: '',
      contactName: '',
      phone: '',
      email: '',
      address: '',
      website: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContractor) {
        await updateContractor(editingContractor.id, formData);
      } else {
        await createContractor(formData);
      }
      await fetchContractors();
      handleCloseDialog();
    } catch (error) {
      alert(error.message || 'Failed to save contractor');
    }
  };

  const handleDeleteClick = (contractor) => {
    setDeletingContractor(contractor);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteContractor(deletingContractor.id);
      await fetchContractors();
      setShowDeleteDialog(false);
      setDeletingContractor(null);
    } catch (error) {
      alert(error.message || 'Failed to delete contractor');
      setShowDeleteDialog(false);
      setDeletingContractor(null);
    }
  };

  const filteredContractors = contractors.filter(contractor => {
    const searchLower = searchQuery.toLowerCase();
    return (
      contractor.companyName?.toLowerCase().includes(searchLower) ||
      contractor.contactName?.toLowerCase().includes(searchLower) ||
      contractor.phone?.toLowerCase().includes(searchLower) ||
      contractor.email?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      <Navbar />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-section text-foreground mb-2">Contractor Directory</h1>
              <p className="text-body">Manage contractor contact information</p>
            </div>
            <Button onClick={() => handleOpenDialog()} className="flex items-center space-x-2">
              <Plus className="h-4 w-4" />
              <span>Add Contractor</span>
            </Button>
          </div>

          <div className="mb-6">
            <Input
              placeholder="Search contractors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="h-4 w-4" />}
              className="w-full sm:w-64"
            />
          </div>

          {loading ? (
            <div className="text-center text-body py-12">Loading...</div>
          ) : filteredContractors.length === 0 ? (
            <div className="glassmorphic p-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">
                {searchQuery ? 'No contractors found' : 'No contractors yet'}
              </h3>
              <p className="text-body mb-4">
                {searchQuery 
                  ? 'Try adjusting your search'
                  : 'Start by adding your first contractor'
                }
              </p>
              {!searchQuery && (
                <Button onClick={() => handleOpenDialog()}>
                  Add Your First Contractor
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredContractors.map((contractor, index) => (
                <motion.div
                  key={contractor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="glassmorphic p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-medium text-foreground mb-1">
                        {contractor.companyName || 'Unnamed Company'}
                      </h3>
                      {contractor.contactName && (
                        <p className="text-sm text-muted-foreground">{contractor.contactName}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDialog(contractor)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(contractor)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {contractor.phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <a href={`tel:${contractor.phone}`} className="text-body hover:text-primary">
                          {contractor.phone}
                        </a>
                      </div>
                    )}
                    {contractor.email && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <a href={`mailto:${contractor.email}`} className="text-body hover:text-primary truncate">
                          {contractor.email}
                        </a>
                      </div>
                    )}
                    {contractor.address && (
                      <div className="flex items-start space-x-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="text-body">{contractor.address}</span>
                      </div>
                    )}
                    {contractor.website && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <a 
                          href={contractor.website.startsWith('http') ? contractor.website : `https://${contractor.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-body hover:text-primary truncate"
                        >
                          {contractor.website}
                        </a>
                      </div>
                    )}
                    {contractor.notes && (
                      <div className="flex items-start space-x-2 text-sm mt-3 pt-3 border-t border-white/10">
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <span className="text-body text-xs">{contractor.notes}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingContractor ? 'Edit Contractor' : 'Add Contractor'}
            </DialogTitle>
            <DialogDescription>
              {editingContractor 
                ? 'Update contractor information below.'
                : 'Enter contractor contact information below.'
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Company Name <span className="text-primary">*</span>
                  </label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="Company Name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Contact Name
                  </label>
                  <Input
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    placeholder="Contact Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Phone
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@company.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Address
                  </label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street Address, City, State ZIP"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Website
                  </label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.example.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes or information..."
                    className="flex w-full rounded-lg border border-input bg-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 min-h-[100px]"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit">
                {editingContractor ? 'Update' : 'Add'} Contractor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contractor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingContractor?.companyName}"? 
              {deletingContractor && ' This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContractorDirectory;

